import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {AnyImageOpContext, ImageOpNode, ImageOpOutputs} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/feature-raycast/definition"
import {Vector2, Vector2Like} from "@cm/math"
import {ImageOpNodeTilingFindBestMatchAlongSegment} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-best-match-along-segment/node"
import {returnType} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-best-match-along-segment/definition"
import {z} from "zod"

@registerNode
export class ImageOpNodeTilingFeatureRayCast extends ImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeTilingFeatureRayCast"
    static definition = definition

    override dispose(): void {
        this.imageOpNodeBag.dispose()
        super.dispose()
    }

    override run = async (evaluator: Evaluator<AnyImageOpContext>): Promise<ImageOpOutputs<typeof definition>> => {
        const {
            imageOpHalOptions,
            sourceImage,
            sourceCastRayOrigin,
            sourceCastRayTarget,
            currentCastRayOrigin,
            currentCastRayTarget,
            idealDistance: idealDistanceOpt,
            searchWidth: searchWidthOpt,
            searchOffset: searchOffsetOpt,
        } = await evaluator.getAll(this.parameters)

        this.imageOpNodeBag.beginFrame()

        const maxCorrelationSamples = 64
        const referenceSegment = {
            from: sourceCastRayOrigin,
            to: sourceCastRayTarget,
            width: 32,
        }
        const referenceSegmentLength = Vector2.distance(referenceSegment.from, referenceSegment.to)

        const currentCastRayDelta = Vector2.sub(currentCastRayTarget, currentCastRayOrigin)
        const currentCastRayLength = currentCastRayDelta.norm()
        if (currentCastRayLength < 1e-6) {
            return undefined
        }
        const currentCentralRayDir = currentCastRayDelta.div(currentCastRayLength)
        const idealDistance = idealDistanceOpt ?? currentCastRayLength
        const maxWiggleIterations = 1 + 2 * 4
        const wiggleStep = 0.5
        const lineSearchResults: z.infer<typeof returnType>[] = []
        for (let wiggleIteration = 1; wiggleIteration <= maxWiggleIterations; wiggleIteration++) {
            const wiggleAmount = wiggleStep * (wiggleIteration >> 1) * ((wiggleIteration & 1) === 0 ? 1 : -1)
            const wiggleDir = currentCentralRayDir.perp()
            const currentTarget = Vector2.add(currentCastRayTarget, wiggleDir.mul(wiggleAmount))
            const currentRayDir = currentTarget.sub(currentCastRayOrigin).normalized()
            const searchOffset = currentRayDir.mul(searchOffsetOpt ?? idealDistance * 0.4) // start the search slightly offset to avoid self-correlation
            const searchWidth = searchWidthOpt ?? idealDistance * 1.2 // pixels to search for with correlation
            const searchEndShift = currentRayDir.mul(referenceSegmentLength + searchWidth)
            const searchSegment = {
                from: Vector2.add(currentCastRayOrigin, searchOffset),
                to: Vector2.add(currentCastRayOrigin, searchOffset).add(searchEndShift),
                width: referenceSegment.width,
            }
            const stride = Math.max(1, Math.round(searchWidth / maxCorrelationSamples))
            const lineSearchPenaltyFn = (position: Vector2Like) => {
                // favor ideal intervals
                const interval = Vector2.sub(position, currentCastRayOrigin).norm()
                const intervalDiff = Math.abs(interval - idealDistance)
                const intervalPenalty = (intervalDiff / idealDistance) * 0.5
                // const intervalPenalty = intervalDiff * 0.01
                // favor similar direction
                const dir = Vector2.sub(position, currentCastRayOrigin).normalized()
                const dot = dir.dot(currentCentralRayDir)
                const anglePenalty = Math.acos(Math.min(1, Math.max(-1, dot))) / (Math.PI / 2)
                return intervalPenalty + anglePenalty
            }
            const promisedLineSearchResult = this.imageOpNodeBag.getNode(
                `findBestMatchAlongSegment${wiggleIteration}`,
                ImageOpNodeTilingFindBestMatchAlongSegment,
                {
                    imageOpHalOptions,
                    sourceImage,
                    referenceSegment,
                    searchSegment,
                    stride,
                    penaltyFn: lineSearchPenaltyFn,
                },
            )
            const lineSearchResult = await evaluator.get(promisedLineSearchResult)
            lineSearchResults.push(lineSearchResult)
        }
        const bestLineSearchResult = lineSearchResults.reduce((best, current) => (current.peakValue > best.peakValue ? current : best))
        const bestPositionAlongLine = bestLineSearchResult.bestMatchPosition

        this.imageOpNodeBag.endFrame()

        if (
            bestPositionAlongLine.x < 0 ||
            bestPositionAlongLine.y < 0 ||
            bestPositionAlongLine.x >= sourceImage.descriptor.width ||
            bestPositionAlongLine.y >= sourceImage.descriptor.height
        ) {
            return undefined
        } else {
            return {
                nextPosition: bestPositionAlongLine,
                details: bestLineSearchResult,
            }
        }
    }

    private imageOpNodeBag = new ImageOpNodeBag("ImageOpNodeTilingFeatureRayCast")
}
